import { useId, useState } from 'react';
import { Checkbox, FormField, Select, TextField } from '../../components';
import type { GenerateQRInput, QRInventoryRow } from './api';
import { useConnectorSearch } from './queries';
import {
  CAMPAIGN_LANGS,
  COLOR_LABEL,
  CONNECTION_LEVEL_LABEL,
  Typeahead,
  apiLang,
  langLabel,
  useDebounced,
} from './parts';

/**
 * The QR section: variant (or the base link), color, language, and the
 * optional connection. Used by the New campaign dialog and by "Add QR code"
 * on a campaign. One code per (variant, color, language) — a second code must
 * change the color or the language.
 */

export interface QrDraft {
  variant: string;
  color: 'primary' | 'secondary';
  lang: string;
  connection: boolean;
  connectionLevel: string;
  connectionMode: 'group' | 'profile';
  connectionGroup: string;
  connectionProfile: { id: string; label: string; detail: string } | null;
}

export function emptyQrDraft(variant = ''): QrDraft {
  return {
    variant,
    color: 'primary',
    lang: CAMPAIGN_LANGS[0] ?? 'en',
    connection: false,
    connectionLevel: '2',
    connectionMode: 'group',
    connectionGroup: '',
    connectionProfile: null,
  };
}

/**
 * A new code for a link that already has a connection code: the same
 * connection (level, group or connector), so a second color or language
 * connects scanners the same way. The newest such code wins.
 */
export function qrDraftForLink(variant: string, codes: QRInventoryRow[]): QrDraft {
  const draft = emptyQrDraft(variant);
  const conn = codes.find((q) => (q.placement ?? '') === variant && q.connectionLevel != null);
  if (!conn) return draft;
  return {
    ...draft,
    connection: true,
    connectionLevel: String(conn.connectionLevel),
    connectionMode: conn.connectionProfile ? 'profile' : 'group',
    connectionGroup: conn.connectionGroup ?? '',
    connectionProfile: conn.connectionProfile
      ? { id: conn.connectionProfile, label: conn.connectionProfileName || conn.connectionProfile, detail: '' }
      : null,
  };
}

export interface VariantChoice {
  value: string;
  label: string;
}

/** Problems with the draft, keyed by field; empty when it can be sent. */
export function qrDraftErrors(
  d: QrDraft,
  opts: { variants: VariantChoice[]; existing?: QRInventoryRow[] },
): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!opts.variants.some((v) => v.value === d.variant)) errors.variant = 'Choose a variant.';
  if (d.connection) {
    if (!d.connectionLevel) errors.connectionLevel = 'Choose a level.';
    if (d.connectionMode === 'group' && !d.connectionGroup.trim()) errors.connectionGroup = 'Name the group.';
    if (d.connectionMode === 'profile' && !d.connectionProfile) errors.connectionProfile = 'Pick a profile.';
  }
  const lang = apiLang(d.lang);
  const clash = opts.existing?.some(
    (q) => (q.placement ?? '') === d.variant && q.color === d.color && (q.lang ?? '') === lang,
  );
  if (clash)
    errors.color = 'A code with this variant, color and language already exists — change the color or language.';
  return errors;
}

export function qrDraftToInput(d: QrDraft, campaignId: string): GenerateQRInput {
  const base = {
    campaignId,
    variant: d.variant,
    color: d.color,
    lang: apiLang(d.lang),
  };
  if (!d.connection) return { ...base, type: 'campaign' };
  return {
    ...base,
    type: 'marketing',
    connectionLevel: Number(d.connectionLevel),
    ...(d.connectionMode === 'group'
      ? { connectionGroup: d.connectionGroup.trim() }
      : { connectionProfile: d.connectionProfile?.id }),
  };
}

export function QrFields({
  draft,
  onChange,
  variants,
  errors = {},
  fixedVariant = false,
}: {
  draft: QrDraft;
  onChange: (next: QrDraft) => void;
  variants: VariantChoice[];
  errors?: Record<string, string>;
  /** The variant is set by where the form is (a variant's card), so no picker. */
  fixedVariant?: boolean;
}) {
  const id = useId();
  const set = (patch: Partial<QrDraft>) => onChange({ ...draft, ...patch });
  const [term, setTerm] = useState('');
  const debounced = useDebounced(term);
  const people = useConnectorSearch(debounced);

  return (
    <div className="grid gap-3.5">
      <div className={fixedVariant ? 'grid grid-cols-2 gap-3.5' : 'grid grid-cols-3 gap-3.5'}>
        {!fixedVariant && (
          <FormField label="Variant" htmlFor={`${id}-variant`} required error={errors.variant}>
            <Select
              id={`${id}-variant`}
              surface="white"
              value={draft.variant}
              options={variants}
              onChange={(e) => set({ variant: e.target.value })}
            />
          </FormField>
        )}
        <FormField label="Color" htmlFor={`${id}-color`} required error={errors.color}>
          <Select
            id={`${id}-color`}
            surface="white"
            value={draft.color}
            options={Object.entries(COLOR_LABEL).map(([value, label]) => ({ value, label }))}
            onChange={(e) => set({ color: e.target.value as QrDraft['color'] })}
          />
        </FormField>
        <FormField label="Language" htmlFor={`${id}-lang`} required>
          <Select
            id={`${id}-lang`}
            surface="white"
            value={draft.lang}
            options={CAMPAIGN_LANGS.map((l) => ({ value: l, label: langLabel(l) }))}
            onChange={(e) => set({ lang: e.target.value })}
          />
        </FormField>
      </div>

      <Checkbox
        checked={draft.connection}
        onChange={(e) => set({ connection: e.target.checked })}
        label="Connection — people who scan this code get connected"
      />

      {draft.connection && (
        <div className="grid gap-3.5 rounded-[10px] bg-cream-warm p-3.5">
          <div className="grid grid-cols-[200px_minmax(0,1fr)] gap-3.5">
            <FormField label="Level" htmlFor={`${id}-level`} required error={errors.connectionLevel}>
              <Select
                id={`${id}-level`}
                surface="white"
                value={draft.connectionLevel}
                options={Object.entries(CONNECTION_LEVEL_LABEL).map(([value, label]) => ({ value, label }))}
                onChange={(e) => set({ connectionLevel: e.target.value })}
              />
            </FormField>
            <FormField label="Connect them to" required>
              <div className="flex gap-4 py-2.5 text-sm">
                {(['group', 'profile'] as const).map((mode) => (
                  <label key={mode} className="inline-flex cursor-pointer items-center gap-2">
                    <input
                      type="radio"
                      name={`${id}-mode`}
                      checked={draft.connectionMode === mode}
                      onChange={() => set({ connectionMode: mode })}
                    />
                    {mode === 'group' ? 'Each other (a group)' : 'One person (a connector)'}
                  </label>
                ))}
              </div>
            </FormField>
          </div>

          {draft.connectionMode === 'group' ? (
            <FormField label="Group name" htmlFor={`${id}-group`} required error={errors.connectionGroup}>
              <TextField
                id={`${id}-group`}
                surface="white"
                value={draft.connectionGroup}
                placeholder="Kermés Unidos por Nuestras Raíces"
                onChange={(e) => set({ connectionGroup: e.target.value })}
              />
            </FormField>
          ) : (
            <FormField
              label="Connector"
              htmlFor={`${id}-profile`}
              required
              error={errors.connectionProfile}
              hint="Search by name, email or phone."
            >
              <Typeahead
                id={`${id}-profile`}
                value={term}
                display={draft.connectionProfile && !term ? draft.connectionProfile.label : undefined}
                onTermChange={(t) => {
                  setTerm(t);
                  if (draft.connectionProfile) set({ connectionProfile: null });
                }}
                onPick={(o) => {
                  const row = people.data?.items.find((p) => p.profileId === o.value);
                  set({
                    connectionProfile: {
                      id: o.value,
                      label: row?.displayName || o.value,
                      detail: [row?.email, row?.phone].filter(Boolean).join(' · '),
                    },
                  });
                  setTerm('');
                }}
                searching={people.isFetching}
                options={(people.data?.items ?? []).map((p) => ({
                  value: p.profileId,
                  label: p.displayName || `${p.firstName} ${p.lastName}`.trim() || p.profileId,
                  detail: [p.email, p.phone].filter(Boolean).join(' · ') || undefined,
                }))}
                placeholder="Search people…"
                invalid={!!errors.connectionProfile}
              />
              {draft.connectionProfile && (
                <div className="mt-1.5 rounded-[10px] border border-panel-border bg-white px-3 py-2 text-[13px]">
                  <div className="font-semibold text-ink">{draft.connectionProfile.label}</div>
                  {draft.connectionProfile.detail && <div className="text-muted">{draft.connectionProfile.detail}</div>}
                </div>
              )}
            </FormField>
          )}
        </div>
      )}
    </div>
  );
}
